/**
 * Piagnet Configuration Schema
 *
 * Defines the configuration structure for Piagnet gateway,
 * agents, channels, and access control policies.
 */

import * as path from "node:path";
import { fileURLToPath } from "node:url";
import { z } from "zod";

/** Get default public directory */
const getDefaultPublicDir = (): string => {
	const __filename = fileURLToPath(import.meta.url);
	const __dirname = path.dirname(__filename);
	// From dist/config/schema.js -> dist/public
	return path.resolve(__dirname, "..", "public");
};

/** Channel scope schema */
export const channelScopeSchema = z.enum(["dm", "group", "thread", "channel"]);

/** Access control policy schema */
export const accessControlPolicySchema = z.object({
	dmPolicy: z.enum(["pairing", "allowlist", "open"]).default("pairing"),
	dmAllowlist: z.array(z.string()).optional(),
	groupPolicy: z.enum(["mention", "allowlist", "open"]).default("mention"),
	groupAllowlist: z.array(z.string()).optional(),
});

/** Channel binding schema */
export const channelBindingSchema = z.object({
	agentId: z.string(),
	channel: z.string(),
	bindType: z.enum(["peer", "guild", "team", "account", "channel", "default"]),
	bindValue: z.string(),
	access: accessControlPolicySchema,
});

/** Channel configuration schema */
export const channelConfigSchema = z.object({
	type: z.string(),
	name: z.string(),
	enabled: z.boolean().default(true),
	config: z.record(z.unknown()).default({}),
	access: accessControlPolicySchema,
});

/** Agent definition schema */
export const agentDefinitionSchema = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string().optional(),
	model: z.object({
		provider: z.string(),
		model: z.string(),
	}),
	systemPrompt: z.string().optional(),
	skills: z.array(z.string()).default([]),
	tools: z
		.object({
			allow: z.array(z.string()).optional(),
			deny: z.array(z.string()).optional(),
		})
		.optional(),
	sandbox: z
		.object({
			enabled: z.boolean().default(false),
			workspace: z.string().optional(),
		})
		.optional(),
	session: z
		.object({
			historyLimit: z.number().default(100),
			dmHistoryLimit: z.number().optional(),
			idleMinutes: z.number().default(60),
			dailyResetHour: z.number().default(4),
			compression: z.enum(["summary", "none"]).default("summary"),
			queuing: z.enum(["sequential", "concurrent", "collect", "steer", "followup"]).default("sequential"),
		})
		.default({
			historyLimit: 100,
			idleMinutes: 60,
			dailyResetHour: 4,
			compression: "summary",
			queuing: "sequential",
		}),
	bootstrap: z
		.object({
			files: z.array(z.string()).default(["AGENTS.md", "SOUL.md", "USER.md", "IDENTITY.md", "TOOLS.md"]),
			maxChars: z.number().default(20000),
		})
		.default({}),
	memory: z
		.object({
			enabled: z.boolean().default(true),
			files: z.array(z.string()).default(["MEMORY.md"]),
		})
		.default({}),
});

/** Gateway configuration schema */
export const gatewayConfigSchema = z.object({
	port: z.number().default(18789),
	wsPath: z.string().default("/ws"),
	publicDir: z.string().default(getDefaultPublicDir()),
	sessionDir: z.string().default("~/.piagnet/sessions"),
	debug: z.boolean().default(false),
	corsOrigins: z.array(z.string()).default(["*"]),
});

/** Node configuration schema */
export const nodeConfigSchema = z.object({
	enabled: z.boolean().default(false),
	discovery: z
		.object({
			mdns: z.boolean().default(true),
			tailnet: z.boolean().default(false),
		})
		.default({}),
	tailnet: z
		.object({
			authKey: z.string().optional(),
		})
		.optional(),
});

/** Full Piagnet configuration schema */
export const piagnetConfigSchema = z.object({
	version: z.string().default("1.0"),
	gateway: gatewayConfigSchema.default({}),
	node: nodeConfigSchema.default({}),
	agents: z.array(agentDefinitionSchema).default([]),
	channels: z.array(channelConfigSchema).default([]),
	bindings: z.array(channelBindingSchema).default([]),
	defaults: z
		.object({
			agent: z.string().optional(),
			model: z
				.object({
					provider: z.string(),
					model: z.string(),
				})
				.optional(),
		})
		.default({}),
});

/** Type exports */
export type ChannelScope = z.infer<typeof channelScopeSchema>;
export type AccessControlPolicy = z.infer<typeof accessControlPolicySchema>;
export type ChannelBinding = z.infer<typeof channelBindingSchema>;
export type ChannelConfig = z.infer<typeof channelConfigSchema>;
export type AgentDefinition = z.infer<typeof agentDefinitionSchema>;
export type GatewayConfig = z.infer<typeof gatewayConfigSchema>;
export type NodeConfig = z.infer<typeof nodeConfigSchema>;
export type PiagnetConfig = z.infer<typeof piagnetConfigSchema>;
export type PiagentConfig = PiagnetConfig; // Backward compatibility

/** Default configuration */
export const defaultConfig: PiagnetConfig = {
	version: "1.0",
	gateway: {
		port: 18789,
		wsPath: "/ws",
		publicDir: getDefaultPublicDir(),
		sessionDir: "~/.piagnet/sessions",
		debug: false,
		corsOrigins: ["*"],
	},
	node: {
		enabled: false,
		discovery: {
			mdns: true,
			tailnet: false,
		},
	},
	agents: [],
	channels: [],
	bindings: [],
	defaults: {},
};

/** Example configuration for documentation */
export const exampleConfig: PiagnetConfig = {
	version: "1.0",
	gateway: {
		port: 18789,
		wsPath: "/ws",
		publicDir: "./public",
		sessionDir: "~/.piagnet/sessions",
		debug: false,
		corsOrigins: ["http://localhost:3000"],
	},
	node: {
		enabled: false,
		discovery: {
			mdns: true,
			tailnet: false,
		},
	},
	agents: [
		{
			id: "assistant",
			name: "AI Assistant",
			description: "General purpose AI assistant",
			model: {
				provider: "anthropic",
				model: "claude-3-5-sonnet-20241022",
			},
			skills: ["coding", "analysis"],
			session: {
				historyLimit: 100,
				idleMinutes: 60,
				dailyResetHour: 4,
				compression: "summary",
				queuing: "sequential",
			},
			bootstrap: {
				files: ["AGENTS.md", "SOUL.md", "USER.md", "IDENTITY.md", "TOOLS.md"],
				maxChars: 20000,
			},
			memory: {
				enabled: true,
				files: ["MEMORY.md"],
			},
		},
	],
	channels: [
		{
			type: "telegram",
			name: "Telegram Bot",
			enabled: true,
			config: {
				botToken: "${TELEGRAM_BOT_TOKEN}",
			},
			access: {
				dmPolicy: "pairing",
				groupPolicy: "mention",
			},
		},
	],
	bindings: [
		{
			agentId: "assistant",
			channel: "telegram",
			bindType: "default",
			bindValue: "*",
			access: {
				dmPolicy: "pairing",
				groupPolicy: "mention",
			},
		},
	],
	defaults: {
		agent: "assistant",
	},
};
